import React, { useRef, useState } from 'react';
import Backbone from 'app/common/vendor/backbone';
import { Popover, usePopover } from 'app/gamma/src/components/popover';
import {
  KeepFromSourceTypes,
  CreateCardFromTemplatePopover,
} from './CreateCardFromTemplatePopover';
import { CardTemplatesPopover } from './CardTemplatesPopover';
import { forTemplate, forNamespace } from 'app/common/lib/util/i18n';

import styles from './CardTemplatesButton.less';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Tooltip } from '@trello/nachos';
import { Feedback } from 'app/src/components/Feedback';

const format = forNamespace('view title');
const listFormat = forTemplate('list');

interface CardTemplatesButtonProps {
  idList: string;
  idBoard: string;
  createCard: (
    name: string,
    idCardSource: string,
    keepFromSource: KeepFromSourceTypes[],
  ) => Promise<() => void>;
}

export const CardTemplatesButton: React.FunctionComponent<CardTemplatesButtonProps> = ({
  idList,
  idBoard,
  createCard,
}) => {
  const ref = useRef(null);
  const popOverName = `CARD_TEMPLATES_POPOVER:${idBoard}:${idList}`;
  const [isEditing, setIsEditing] = useState(false);
  const { isPopoverShown, closePopover, showPopover } = usePopover({
    name: popOverName,
  });

  return (
    <>
      <Tooltip
        content={isPopoverShown ? null : listFormat('create-from-template')}
        delay={100}
        hideTooltipOnMouseDown
      >
        <a
          ref={ref}
          onClick={() => {
            if (isPopoverShown) {
              closePopover();
            } else {
              showPopover();
              trackUe({
                category: Noun.CARD_TEMPLATES,
                verb: Verb.OPENS,
                directObj: Noun.CARD_TEMPLATES_MENU,
                context: {
                  entry: Noun.LIST,
                },
              });
            }
          }}
          role="button"
          className="icon-sm icon-template-card dark-background-hover"
        />
      </Tooltip>

      {isPopoverShown ? (
        <Popover
          title={format('card templates beta')}
          name={popOverName}
          target={() => ref.current}
          onClose={() => {
            closePopover();
            setIsEditing(false);
          }}
          className={styles.popover}
          contentClassName={styles.popoverContent}
        >
          {({ push, pop, pushConfirm }) => {
            return (
              <CardTemplatesPopover
                idList={idList}
                idBoard={idBoard}
                onSelect={(idCardTemplate) =>
                  push(
                    <CreateCardFromTemplatePopover
                      idCard={idCardTemplate}
                      createCard={async (
                        name: string,
                        idCardSource: string,
                        keepFromSource: KeepFromSourceTypes[],
                      ) => {
                        try {
                          const fnScrollToNewCard = await createCard(
                            name,
                            idCardSource,
                            keepFromSource,
                          );
                          trackUe({
                            category: Noun.CARD_TEMPLATES,
                            verb: Verb.COMPLETES,
                            directObj: Noun.CREATE_CARD_FROM_TEMPLATE_MENU,
                            context: {
                              entry: Noun.LIST,
                            },
                          });
                          closePopover();
                          fnScrollToNewCard();
                        } catch (e) {
                          // `createCard` handles clean up in ListView.
                        }
                      }}
                    />,
                    { title: format('create card') },
                  )
                }
                onTemplateCreated={(card: { shortLink: string }) => {
                  closePopover();
                  Backbone.history.navigate(`/c/${card.shortLink}`, {
                    trigger: true,
                  });
                }}
                confirmDelete={(onConfirm: () => void) =>
                  pushConfirm({
                    confirmKey: 'delete template card',
                    confirmCallback: () => {
                      onConfirm();
                      pop();
                    },
                    isDanger: true,
                  })
                }
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                customGetFeedback={(options) => {
                  return (e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    e.preventDefault();

                    push(
                      <Feedback
                        key="feedback"
                        {...options}
                        onClose={() => pop()}
                      />,
                      { title: format('feedback') },
                    );
                  };
                }}
              />
            );
          }}
        </Popover>
      ) : null}
    </>
  );
};
